import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import Typography from "@mui/material/Typography";
import { AxiosError } from "axios";
import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { CreateUser, LoginData, UserDto } from "../courses/services/type";
import { useCreateLogin } from "../courses/services/use-create-login";
import { useLogin } from "../courses/services/use-login";

// Définition du type des données utilisateur
interface User {
    user: UserDto | null;
    login: (data: LoginData, onSuccess?: () => void) => void;
    createUser: (data: CreateUser, onSuccess?: () => void) => void
    logout: (onSuccess?: () => void) => void;
}

// Création du contexte utilisateur avec une valeur par défaut
const UserContext = createContext<User | undefined>(undefined);

// Hook personnalisé pour utiliser le contexte utilisateur
export const useUser = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error("useUser doit être utilisé à l'intérieur d'un UserProvider");
    }
    return context;
};

// Provider qui stocke les informations utilisateur
export const UserProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<UserDto | null>(() => {
        const storedUser = localStorage.getItem("user");
        return storedUser ? JSON.parse(storedUser) : null;
    });
    const { mutate: loginUser } = useLogin();
    const { mutate: createLogin } = useCreateLogin();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [validationMessage, setValidationMessage] = useState<string | null>(null);

    useEffect(() => {
        if (user) {
            if (!user.token || user.token.length <= 0) {
                logout()
            }
            const payload = JSON.parse(atob(user.token.split('.')[1]));
            const exp = payload.exp * 1000;
            const timeout = exp - Date.now();

            if (timeout > 0) {
                const timer = setTimeout(() => {
                    logout();
                }, timeout);

                return () => clearTimeout(timer);
            } else {
                logout();
            }
        }
    }, [user]);


    // Fonction de connexion
    const login = (data: LoginData, onSuccess?: () => void) => {
        loginUser(data, {
            onSuccess: ({ email, pseudo, token }) => {
                const userData = { email, pseudo, token };
                // Stocker dans le state
                setUser(userData);
                // Stocker dans le localStorage
                localStorage.setItem("user", JSON.stringify(userData));
                onSuccess?.();
            },
            onError: (err: AxiosError) => {
                console.error('Erreur de connexion', err);
                if (err.response?.status === 401) {
                    setErrorMessage('Email ou mot de passe incorrect');
                } else {
                    setErrorMessage('Une erreur est survenue. Veuillez réessayer.');
                }
            },
        });
    };

    const createUser = (data: CreateUser, onSuccess?: () => void) => {
        createLogin(data, {
            onSuccess: () => {
                setValidationMessage("Bienvenue chez Hippoprono, compte créé avec succès")
                onSuccess?.();
            },
            onError: (err: AxiosError) => {
                console.error('Erreur de connexion', err);
                if (err.response?.status === 401) {
                    setErrorMessage('Email ou mot de passe incorrect');
                } else if (err.response?.status === 409) {
                    setErrorMessage('Email déjà utilisé');
                }
                else {
                    setErrorMessage('Une erreur est survenue. Veuillez réessayer.');
                }
            },
        });
    }

    // Fonction de déconnexion
    const logout = (onSuccess?: () => void) => {
        setUser(null)
        localStorage.removeItem("user");
        onSuccess?.();
    };

    return (
        <UserContext.Provider value={{ user, login, logout, createUser }}>
            {children}
            {errorMessage && (
                <Snackbar
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    open={!!errorMessage}
                    autoHideDuration={8000}
                    onClose={() => setErrorMessage(null)}
                >
                    <Alert onClose={() => setErrorMessage(null)} severity="error" variant="filled">
                        <Typography>{errorMessage}</Typography>
                    </Alert>
                </Snackbar>
            )}
            {validationMessage && (
                <Snackbar
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    open={!!validationMessage}
                    autoHideDuration={8000}
                    onClose={() => setValidationMessage(null)}
                >
                    <Alert onClose={() => setValidationMessage(null)} severity="success" variant="filled">
                        <Typography>{validationMessage}</Typography>
                    </Alert>
                </Snackbar>
            )}
        </UserContext.Provider>
    );
};
